import React from 'react';
import { Box, Typography, Button, Container,Grid,  Chip } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import bgImage from '../../assets/bg.avif';
import rightImage from '../../assets/right.avif';

const Hero = () => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });

    // Reversed parallax transforms: elements move UP when scrolling DOWN
    const yText = useTransform(scrollYProgress, [0, 1], [0, -200]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, -150]);
    const yBg = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const opacityHero = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                minHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                backgroundColor: '#fbfaf8', // Base color
            }}
        >
            {/* Parallax Background Layer */}
            <Box
                component={motion.div}
                style={{ y: yBg }}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: -100, // Extra height for parallax movement
                    zIndex: 0,
                    background: `linear-gradient(to bottom, rgba(255, 255, 255, 0) 70%, rgba(255, 255, 255, 1) 100%), url(${bgImage}) center/cover no-repeat`,
                }}
            />
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Left Side: Content with Entrance + Parallax Animation */}
                    <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        component={motion.div}
                        style={{ y: yText, opacity: opacityHero }}
                        initial={{ x: -60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Box sx={{ maxWidth: 650, pl: { md: 4 } }}>
                            <Chip
                                label="NEW COLLECTION 2024"
                                sx={{
                                    mb: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(5px)',
                                    border: '1px solid #3333334d',
                                    fontFamily: "'Sora', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.05em',
                                    borderRadius: '50px',
                                    px: 2,
                                    height: '36px'
                                }}
                            />

                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="h1"
                                    className="font-heading"
                                    sx={{
                                        fontFamily: "'cormorantgaramond', serif",
                                        fontWeight: 800,
                                        fontSize: { xs: '4.5rem', lg: '6.5rem' },
                                        lineHeight: 1,
                                        color: '#000',
                                        mb: 0,
                                        letterSpacing: '0.04em',
                                        textRendering: 'optimizeLegibility',
                                        WebkitFontSmoothing: 'antialiased'
                                    }}
                                >
                                    Where Style
                                </Typography>
                                <Typography
                                    variant="h1"
                                    className="font-heading"
                                    sx={{
                                        fontFamily: "'cormorantgaramond', serif",
                                        fontWeight: 800,
                                        fontStyle: 'italic',
                                        fontSize: { xs: '4.8rem', lg: '6.0rem' },
                                        lineHeight: 1,
                                        color: '#d4a3a3',
                                        ml: { md: 2 },
                                        letterSpacing: '0.04em'
                                    }}
                                >
                                    Blossoms
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontFamily: "'Sora', sans-serif",
                                    fontSize: '1.2rem',
                                    fontWeight: 600,
                                    color: '#1a1a1a',
                                    mb: 6,
                                    lineHeight: 1.6,
                                    maxWidth: 480
                                }}
                            >
                                Discover premium ethnic an western wear designed for the modern Indian woman. Elegance meets affordability in every stitch.
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#2d2d2d',
                                        color: 'white',
                                        px: 6,
                                        py: 2,
                                        borderRadius: '50px',
                                        fontFamily: "'Sora', sans-serif",
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        '&:hover': { backgroundColor: '#000' }
                                    }}
                                >
                                    SHOP COLLECTION
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        borderColor: '#2d2d2d',
                                        color: '#2d2d2d',
                                        px: 6,
                                        py: 2,
                                        borderRadius: '50px',
                                        fontFamily: "'Sora', sans-serif",
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        '&:hover': { borderColor: '#000', backgroundColor: 'rgba(0,0,0,0.03)' }
                                    }}
                                >
                                    VIEW LOOKBOOK
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Right Side: Framed Image with Pop-up + Parallax Animation */}
                    <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', pr: { lg: 8 } }}
                        component={motion.div}
                        style={{ y: yCard }}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Box
                            sx={{
                                backgroundColor: 'white',
                                mt: 10,
                                p: 1,
                                position: 'relative',
                                border: '20px solid white',
                                boxShadow: '20px 20px 60px rgba(0,0,0,0.08)',
                                maxWidth: '100%',
                                display: 'inline-block',
                                cursor: 'crosshair',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -8,
                                    left: -8,
                                    right: -8,
                                    bottom: -8,
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    pointerEvents: 'none'
                                },
                                '&:hover img': {
                                    filter: 'grayscale(0%) contrast(1)',
                                }
                            }}
                        >
                            <Box
                                component="img"
                                src={rightImage}
                                alt="Fashion showcase"
                                loading="eager"
                                sx={{
                                    width: '100%',
                                    maxWidth: 380,
                                    height: 'auto',
                                    display: 'block',
                                    filter: 'grayscale(100%) contrast(1.1)',
                                    transition: 'filter 0.8s ease-in-out',
                                }}
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=400&q=80'; }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Hero;
